import { Component } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { Audience, Transfer } from "src/app/models/services/audience";
import { GroupShare } from "src/app/models/services/admin/groups-share/groupShare";
import {
  ShareAudience,
  ShareAudienceType,
  TransferShareAudience,
  TransferShareBusiness,
  TransferShareItem,
  TransferSharePlatform,
  UpdateSharedAudienceRequest
} from "src/app/models/services/transfer-share-audience.model";
import { GroupShareService } from "src/app/services/admin/group-share.service";
export type ShareItem = ShareAudience;
export type { ShareAudienceType, TransferShareAudience, TransferShareBusiness, TransferShareItem, TransferSharePlatform, UpdateSharedAudienceRequest };

type Filter = "all" | "group" | "business" | "item";
type Context = "available" | "selected";
type LeafType = "account" | "advertiser";
type LeafItem = ShareItem & { type: LeafType };

@Component({
  selector: "transfers-share-dialog",
  templateUrl: "./transfers-share-dialog.component.html",
  styleUrls: ["./transfers-share-dialog.component.sass"]
})
export class TransfersShareDialogComponent {
  public loading = false;
  public form: FormGroup;
  public availableFilter: Filter = "all";

  public audience!: Audience;
  public transfer!: Transfer;
  public accept?: (request: UpdateSharedAudienceRequest) => void;
  public dismiss?: () => void;

  public selectedItems: ShareItem[] = [];

  public availablePageIndex = 0;
  public availableTotalPages = 1;
  public availablePagedGroups: ShareItem[] = [];
  public availablePagedBusinesses: ShareItem[] = [];
  public availablePagedRootItems: ShareItem[] = [];

  public selectedPageIndex = 0;
  public selectedTotalPages = 1;
  public selectedPagedGroups: ShareItem[] = [];
  public selectedPagedBusinesses: ShareItem[] = [];
  public selectedPagedRootItems: ShareItem[] = [];

  private sourceData: TransferShareAudience = { platforms: [] };
  private allItems: ShareItem[] = [];
  private initialSelectedKeys = new Set<string>();

  private availableSearch = "";
  private availableSearchRaw = "";
  private selectedSearch = "";

  private availableChecked = new Set<string>();
  private selectedChecked = new Set<string>();

  private expandedAvailableGroups = new Set<string>();
  private expandedAvailableBusinesses = new Set<string>();
  private expandedSelectedGroups = new Set<string>();
  private expandedSelectedBusinesses = new Set<string>();

  private readonly pageSize = 5;

  constructor(
    fb: FormBuilder,
    private groupShareService: GroupShareService
  ) {
    this.form = fb.group({});
  }

  public get dialogTitle(): string {
    return "Edit Share Items";
  }

  public get canShare(): boolean {
    return !this.form.invalid && (this.hasSelectionChanges() || this.hasRetryableErrors());
  }

  public get canAddSelectedItems(): boolean {
    return this.resolveLeaves(this.getCheckedNodes("available"), "available").length > 0;
  }

  public get canRemoveSelectedItems(): boolean {
    return this.resolveLeaves(this.getCheckedNodes("selected"), "selected").length > 0;
  }

  public get manualItemId(): string {
    return this.availableSearchRaw.trim();
  }

  public get canAddManualItem(): boolean {
    const id = this.manualItemId;
    return !!id && ![...this.allItems, ...this.selectedItems].some(item => String(item.id) === id);
  }

  public get isAvailableHeaderChecked(): boolean {
    const items = this.getDisplayedAvailableItems();
    return items.length > 0 && items.every(item => this.availableChecked.has(this.key(item)));
  }

  public get isAvailableHeaderIndeterminate(): boolean {
    const items = this.getDisplayedAvailableItems();
    const count = items.filter(item => this.availableChecked.has(this.key(item))).length;
    return count > 0 && count < items.length;
  }

  public init(data: TransferShareAudience): void {
    this.loading = true;
    this.resetUi();
    const baseData = this.normalizeData(data);
    const platformId = this.getPlatformId(baseData);

    if (platformId == null) {
      this.initialize(baseData);
      this.loading = false;
      return;
    }

    this.groupShareService.getGroups().pipe(
      catchError(error => {
        console.error("Error loading Group Shares", error);
        return of([] as GroupShare[]);
      }),
      finalize(() => this.loading = false)
    ).subscribe(groups => {
      this.initialize(this.addSavedGroups(baseData, groups, platformId));
    });
  }

  public addManualItem(): void {
    if (!this.canAddManualItem) return;
    this.selectedItems = this.uniqueLeaves([
      ...this.selectedItems,
      { id: this.manualItemId, name: this.manualItemId, type: "advertiser" } as ShareItem
    ]);
    this.afterSelectionChange();
  }

  public onDismiss(): void {
    this.dismiss?.();
  }

  public onAccept(): void {
    if (!this.canShare) return;
    const request = this.buildRequest();
    if (request) this.accept?.(request);
  }

  public readonly trackByItem = (_: number, item: ShareItem): string => this.key(item);

  public getItemTypeLabel(item: ShareItem): string {
    return ({
      "saved-group": "Group",
      business: "Business",
      account: "Ad account",
      advertiser: "Advertiser"
    } as Partial<Record<ShareAudienceType, string>>)[item.type] ?? item.type ?? "";
  }

  public hasItemError(item?: ShareItem | null): boolean {
    return item?.isError === true;
  }

  public getItemErrorMessage(item?: ShareItem | null): string {
    return item?.errorMessage ?? "";
  }

  public hasBusinessChildError(business?: ShareItem | null): boolean {
    return !!business && this.getSelectedItemsFromBusiness(business).some(item => item.isError === true);
  }

  public hasGroupChildError(group?: ShareItem | null): boolean {
    return !!group && this.getGroupLeaves(group, "selected").some(item => item.isError === true);
  }

  public getBusinessErrorTooltip(business?: ShareItem | null): string {
    return business ? this.errorMessages(this.getSelectedItemsFromBusiness(business)) : "";
  }

  public getGroupErrorTooltip(group?: ShareItem | null): string {
    return group ? this.errorMessages(this.getGroupLeaves(group, "selected")) : "";
  }

  public onAvailableItemRowClick(event: MouseEvent, item: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleAvailableSelection(item, !this.isAvailableSelected(item));
  }

  public onSelectedItemRowClick(event: MouseEvent, item: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleSelectedSelection(item, !this.isSelectedSelected(item));
  }

  public onGroupRowClick(event: MouseEvent, group: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleExpanded(this.expandedAvailableGroups, group.id);
  }

  public onBusinessRowClick(event: MouseEvent, business: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleExpanded(this.expandedAvailableBusinesses, business.id);
  }

  public onSelectedGroupRowClick(event: MouseEvent, group: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleExpanded(this.expandedSelectedGroups, group.id);
  }

  public onSelectedBusinessRowClick(event: MouseEvent, business: ShareItem): void {
    if (!this.isInteractive(event)) this.toggleExpanded(this.expandedSelectedBusinesses, business.id);
  }

  public isGroupExpanded(group: ShareItem): boolean {
    return this.expandedAvailableGroups.has(String(group.id));
  }

  public isBusinessExpanded(business: ShareItem): boolean {
    return this.expandedAvailableBusinesses.has(String(business.id));
  }

  public isSelectedGroupExpanded(group: ShareItem): boolean {
    return this.expandedSelectedGroups.has(String(group.id));
  }

  public isSelectedBusinessExpanded(business: ShareItem): boolean {
    return this.expandedSelectedBusinesses.has(String(business.id));
  }

  public onSearchAvailable(value: string): void {
    this.availableSearchRaw = (value ?? "").trim();
    this.availableSearch = this.normalizeText(value);
    this.availablePageIndex = 0;
    this.rebuild();
  }

  public onSearchSelected(value: string): void {
    this.selectedSearch = this.normalizeText(value);
    this.selectedPageIndex = 0;
    this.rebuild();
  }

  public setAvailableFilter(filter: Filter): void {
    this.availableFilter = filter;
    this.availablePageIndex = 0;
    this.rebuildAvailablePage();
  }

  public isAvailableSelected(item: ShareItem): boolean {
    return this.availableChecked.has(this.key(item));
  }

  public toggleAvailableSelection(item: ShareItem, checked: boolean): void {
    this.setBranchChecked(item, checked, "available");
    this.syncContainerChecks("available");
  }

  public toggleGroupSelection(group: ShareItem, checked: boolean): void {
    this.toggleAvailableSelection(group, checked);
  }

  public toggleBusinessSelection(business: ShareItem, checked: boolean): void {
    this.toggleAvailableSelection(business, checked);
  }

  public toggleAvailableHeaderSelection(checked: boolean): void {
    this.getDisplayedAvailableItems().forEach(item => this.setBranchChecked(item, checked, "available"));
    this.syncContainerChecks("available");
  }

  public addAllAvailable(): void {
    const selected = this.selectedLeafKeys();
    const items = this.getLeaves().filter(item => !selected.has(this.key(item)));
    if (!items.length) return;

    this.selectedItems = this.uniqueLeaves([...this.selectedItems, ...items]);
    this.afterSelectionChange();
  }

  public addSelected(): void {
    const items = this.resolveLeaves(this.getCheckedNodes("available"), "available");
    if (!items.length) return;

    this.selectedItems = this.uniqueLeaves([...this.selectedItems, ...items]);
    this.afterSelectionChange();
  }

  public isSelectedSelected(item: ShareItem): boolean {
    return this.selectedChecked.has(this.key(item));
  }

  public toggleSelectedSelection(item: ShareItem, checked: boolean): void {
    this.setBranchChecked(item, checked, "selected");
    this.syncContainerChecks("selected");
  }

  public removeAllSelected(): void {
    if (!this.selectedItems.length) return;
    this.selectedItems = [];
    this.afterSelectionChange();
  }

  public removeSelected(): void {
    const items = this.resolveLeaves(this.getCheckedNodes("selected"), "selected");
    if (items.length) this.removeItems(items);
  }

  public removeSingle(item: ShareItem): void {
    const items = this.resolveLeaves([item], "selected");
    if (items.length) this.removeItems(items);
  }

  public getBusinesses(group: ShareItem): ShareItem[] {
    return this.getChildBusinesses(group, "available", true);
  }

  public getBusinessItems(business: ShareItem): ShareItem[] {
    return this.getBusinessLeaves(business, "available", true);
  }

  public getGroupItemsCount(group: ShareItem): number {
    return this.getGroupLeaves(group, "available").length;
  }

  public getBusinessItemsCount(business: ShareItem): number {
    return this.getBusinessLeaves(business, "available").length;
  }

  public getSelectedBusinesses(group: ShareItem): ShareItem[] {
    return this.getChildBusinesses(group, "selected", true);
  }

  public getSelectedItemsFromBusiness(business: ShareItem): ShareItem[] {
    return this.getBusinessLeaves(business, "selected", true);
  }

  public getSelectedChildrenCount(group: ShareItem): number {
    return this.getGroupLeaves(group, "selected").length;
  }

  public getSelectedBusinessChildrenCount(business: ShareItem): number {
    return this.getBusinessLeaves(business, "selected").length;
  }

  public goToPrevAvailablePage(): void {
    this.availablePageIndex = Math.max(0, this.availablePageIndex - 1);
    this.rebuildAvailablePage();
  }

  public goToNextAvailablePage(): void {
    this.availablePageIndex = Math.min(this.availableTotalPages - 1, this.availablePageIndex + 1);
    this.rebuildAvailablePage();
  }

  public goToPrevSelectedPage(): void {
    this.selectedPageIndex = Math.max(0, this.selectedPageIndex - 1);
    this.rebuildSelectedPage();
  }

  public goToNextSelectedPage(): void {
    this.selectedPageIndex = Math.min(this.selectedTotalPages - 1, this.selectedPageIndex + 1);
    this.rebuildSelectedPage();
  }

  // -------------------------------------------------------------------------
  // Data loading / mapping
  // -------------------------------------------------------------------------
  private initialize(data: TransferShareAudience): void {
    this.sourceData = data;
    this.allItems = this.flatten(data);
    this.selectedItems = this.getLeaves().filter(item => item.selected === true);
    this.initialSelectedKeys = this.selectedLeafKeys();
    this.getLeaves().forEach(item => item.selected = false);
    this.rebuild();
  }

  private addSavedGroups(data: TransferShareAudience,groups: GroupShare[],platformId: string | number): TransferShareAudience {
    const source = this.clone(data);
    const state = this.getLeafState(source);
    const savedGroups = (groups ?? [])
      .filter(group => Number(group.targetPlatform) === Number(platformId))
      .filter(group => group?.id != null)
      .map(group => this.mapSavedGroup(group, platformId, state));
    const platform = source.platforms?.find(item => Number(item.id) === Number(platformId));
    if (!platform) return source;
    const savedIds = new Set(savedGroups.map(group => String(group.id)));
    platform.groups = [...savedGroups,...(platform.groups ?? []).filter(group => !savedIds.has(String(group.id)))] as any;
    return source;
  }

  private mapSavedGroup(group: GroupShare,platformId: string | number,state: Map<string, TransferShareItem>): any {
    const groupId = group.id as string | number;
    return {
      id: groupId,
      name: group.name ?? "",
      type: "saved-group",
      parentId: platformId,
      businesses: (group.businesses ?? []).map((business: any) => {
        const realBusinessId = business.id;
        const uiBusinessId = `group:${groupId}:business:${realBusinessId}`;
        const items = business.items ?? business.advertisers ?? [];

        return {
          id: uiBusinessId,
          name: business.name ?? "",
          type: "business",
          parentId: groupId,
          items: items.map((item: any) => {
            const type = this.leafType(item.type);
            const previous = state.get(this.leafKey(type, item.id));

            return {
              ...item,
              id: item.id,
              name: item.name ?? previous?.name ?? "",
              type,
              parentId: uiBusinessId,
              businessId: realBusinessId,
              businessName: business.name ?? "",
              audienceId: item.audienceId ?? previous?.audienceId,
              selected: previous?.selected === true,
              isError: previous?.isError === true,
              errorMessage: previous?.errorMessage ?? ""
            };
          })
        };
      })
    };
  }

  private normalizeData(data: TransferShareAudience): TransferShareAudience {
    const source: any = this.clone(data ?? { platforms: [] });
    if (!Array.isArray(source.platforms) || !source.platforms.length) {
      source.platforms = [{
        id: source.platformId ?? source.targetPlatformId ?? source.id ?? "platform",
        name: source.platformName ?? source.name ?? "",
        type: "platform",
        businesses: source.businesses ?? [],
        groups: source.groups ?? [],
        items: source.items ?? []
      }];
    }
    source.platforms = source.platforms.map((platform: any) => {
      const platformId = platform.id ?? platform.platformId ?? source.platformId ?? "platform";
      const normalizeBusiness = (business: any, parentId: any) => ({
        ...business,
        type: "business",
        parentId: business.parentId ?? parentId,
        items: (business.items ?? []).map((item: any) => this.normalizeLeaf(item, business.id, business))
      });
      return {
        ...platform,
        id: platformId,
        type: "platform",
        businesses: (platform.businesses ?? []).map((business: any) => normalizeBusiness(business, platformId)),
        groups: (platform.groups ?? []).map((group: any) => ({
          ...group,
          type: "saved-group",
          parentId: group.parentId ?? platformId,
          businesses: (group.businesses ?? []).map((business: any) => normalizeBusiness(business, group.id))
        })),
        items: (platform.items ?? []).map((item: any) => this.normalizeLeaf(item, platformId))
      };
    });
    return source as TransferShareAudience;
  }

  private normalizeLeaf(item: any, parentId: any, business?: any): TransferShareItem {
    return {
      ...item,
      type: this.leafType(item.type),
      parentId: item.parentId ?? parentId,
      selected: item.selected === true,
      businessId: item.businessId ?? business?.id,
      businessName: item.businessName ?? business?.name,
      audienceId: item.audienceId,
      isError: item.isError === true,
      errorMessage: item.errorMessage ?? ""
    } as TransferShareItem;
  }

  private flatten(data: TransferShareAudience): ShareItem[] {
    const result: ShareItem[] = [];
    const seen = new Set<string>();
    const add = (item: ShareItem): void => {
      if (item?.id == null) return;
      const key = this.key(item);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(item);
      }
    };
    for (const platform of data.platforms ?? []) {
      for (const group of platform.groups ?? []) {
        add(this.node(group.id, group.name, "saved-group", group.parentId ?? platform.id, group.info));
        for (const business of group.businesses ?? []) {
          add(this.node(business.id, business.name, "business", business.parentId ?? group.id, business.info));
          for (const item of business.items ?? []) add(this.flatLeaf(item, business));
        }
      }
      for (const business of platform.businesses ?? []) {
        add(this.node(business.id, business.name, "business", business.parentId ?? platform.id, business.info));
        for (const item of business.items ?? []) add(this.flatLeaf(item, business));
      }
      for (const item of ((platform as any).items ?? [])) {
        add(this.flatLeaf(item));
      }
    }
    return this.sort(result);
  }

  private flatLeaf(item: TransferShareItem, business?: TransferShareBusiness): LeafItem {
    return {
      id: item.id,
      name: item.name,
      type: this.leafType(item.type),
      parentId: item.parentId,
      info: item.info,
      businessId: item.businessId ?? business?.id,
      businessName: item.businessName ?? business?.name,
      audienceId: item.audienceId,
      audienceName: item.audienceName,
      selected: item.selected === true,
      isError: item.isError === true,
      errorMessage: item.errorMessage ?? ""
    } as LeafItem;
  }

  private node(id: any, name: any, type: ShareAudienceType, parentId?: any, info?: any): ShareItem {
    return { id, name: name ?? "", type, parentId, info } as ShareItem;
  }

  private getLeafState(data: TransferShareAudience): Map<string, TransferShareItem> {
    const result = new Map<string, TransferShareItem>();
    const add = (item: TransferShareItem) => {
      const key = this.leafKey(item.type, item.id);
      const current = result.get(key);
      if (!current || item.selected || item.isError) result.set(key, item);
    };
    for (const platform of data.platforms ?? []) {
      ((platform as any).items ?? []).forEach(add);
      (platform.businesses ?? []).forEach(business => (business.items ?? []).forEach(add));
      (platform.groups ?? []).forEach(group =>
        (group.businesses ?? []).forEach(business => (business.items ?? []).forEach(add))
      );
    }
    return result;
  }

  // -------------------------------------------------------------------------
  // Tree helpers
  // -------------------------------------------------------------------------
  private getGroups(): ShareItem[] {
    return this.allItems.filter(item => item.type === "saved-group");
  }

  private getAllBusinesses(): ShareItem[] {
    return this.allItems.filter(item => item.type === "business");
  }

  private getLeaves(): LeafItem[] {
    return this.allItems.filter((item): item is LeafItem => this.isLeaf(item));
  }

  private getRootBusinesses(): ShareItem[] {
    const groupIds = new Set(this.getGroups().map(group => String(group.id)));
    return this.getAllBusinesses().filter(business => !groupIds.has(String(business.parentId)));
  }

  private getChildBusinesses(group: ShareItem, context: Context, applySearch = false): ShareItem[] {
    const search = context === "available" ? this.availableSearch : this.selectedSearch;
    return this.sort(
      this.getAllBusinesses()
        .filter(business => String(business.parentId) === String(group.id))
        .filter(business => this.getBusinessLeaves(business, context).length > 0)
        .filter(business => !applySearch || !search || this.matchesBusiness(business, context))
    );
  }

  private getBusinessLeaves(business: ShareItem, context: Context, applySearch = false): ShareItem[] {
    const selected = this.selectedLeafKeys();
    const search = context === "available" ? this.availableSearch : this.selectedSearch;
    const source = context === "available" ? this.getLeaves() : this.selectedItems.filter(item => this.isLeaf(item));
    return this.sort(
      source
        .filter(item => String(item.parentId) === String(business.id))
        .filter(item => context === "selected" || !selected.has(this.key(item)))
        .filter(item => !applySearch || !search || this.matches(item, search))
    );
  }

  private getGroupLeaves(group: ShareItem, context: Context): ShareItem[] {
    const leaves = this.getChildBusinesses(group, context).reduce(
      (result: ShareItem[], business) => [
        ...result,
        ...this.getBusinessLeaves(business, context)
      ],
      []
    );
    return this.uniqueLeaves(leaves);
  }

  private getRootLeaves(context: Context): ShareItem[] {
    const businessIds = new Set(this.getAllBusinesses().map(item => String(item.id)));
    const selected = this.selectedLeafKeys();
    const search = context === "available" ? this.availableSearch : this.selectedSearch;
    const source = context === "available" ? this.getLeaves() : this.selectedItems.filter(item => this.isLeaf(item));
    return source
      .filter(item => !businessIds.has(String(item.parentId)))
      .filter(item => context === "selected" || !selected.has(this.key(item)))
      .filter(item => !search || this.matches(item, search));
  }

  private resolveLeaves(items: ShareItem[], context: Context): ShareItem[] {
    const leaves = items.reduce((result: ShareItem[], item) => {
      if (item.type === "saved-group") {
        return [...result, ...this.getGroupLeaves(item, context)];
      }
      if (item.type === "business") {
        return [...result, ...this.getBusinessLeaves(item, context)];
      }
      if (this.isLeaf(item)) {
        return [...result, item];
      }
      return result;
    }, []);
    return this.uniqueLeaves(leaves);
  }

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  private setBranchChecked(item: ShareItem, checked: boolean, context: Context): void {
    const store = context === "available" ? this.availableChecked : this.selectedChecked;
    this.setChecked(store, this.key(item), checked);
    if (item.type === "saved-group") {
      this.getChildBusinesses(item, context).forEach(business => {
        this.setChecked(store, this.key(business), checked);
        this.getBusinessLeaves(business, context).forEach(leaf => this.setChecked(store, this.key(leaf), checked));
      });
    } else if (item.type === "business") {
      this.getBusinessLeaves(item, context).forEach(leaf => this.setChecked(store, this.key(leaf), checked));
    }
  }

  private syncContainerChecks(context: Context): void {
    const store = context === "available" ? this.availableChecked : this.selectedChecked;
    this.getAllBusinesses().forEach(business => {
      const leaves = this.getBusinessLeaves(business, context);
      this.setChecked(store, this.key(business), leaves.length > 0 && leaves.every(item => store.has(this.key(item))));
    });
    this.getGroups().forEach(group => {
      const leaves = this.getGroupLeaves(group, context);
      this.setChecked(store, this.key(group), leaves.length > 0 && leaves.every(item => store.has(this.key(item))));
    });
  }

  private getCheckedNodes(context: Context): ShareItem[] {
    const store = context === "available" ? this.availableChecked : this.selectedChecked;
    const universe = [...this.allItems, ...this.selectedItems];
    return this.unique(universe).filter(item => store.has(this.key(item)));
  }

  private removeItems(items: ShareItem[]): void {
    const keys = new Set(items.map(item => this.key(item)));
    this.selectedItems = this.selectedItems.filter(item => !keys.has(this.key(item)));
    this.afterSelectionChange();
  }

  private afterSelectionChange(): void {
    this.availableChecked.clear();
    this.selectedChecked.clear();
    this.availablePageIndex = 0;
    this.selectedPageIndex = 0;
    this.rebuild();
  }

  // -------------------------------------------------------------------------
  // Search / paging
  // -------------------------------------------------------------------------
  private rebuild(): void {
    this.rebuildAvailablePage();
    this.rebuildSelectedPage();
  }

  private rebuildAvailablePage(): void {
    const groups = this.getGroups()
      .filter(group => this.getGroupLeaves(group, "available").length > 0)
      .filter(group => !this.availableSearch || this.matchesGroup(group, "available"));
    const businesses = this.getRootBusinesses()
      .filter(business => this.getBusinessLeaves(business, "available").length > 0)
      .filter(business => !this.availableSearch || this.matchesBusiness(business, "available"));
    const rootItems = this.getRootLeaves("available");
    const byFilter: Record<Filter, ShareItem[]> = {
      all: [...groups, ...businesses, ...rootItems],
      group: groups,
      business: businesses,
      item: rootItems
    };
    this.setAvailablePage(this.sort(byFilter[this.availableFilter]));
  }

  private rebuildSelectedPage(): void {
    const groups = this.getGroups()
      .filter(group => this.getGroupLeaves(group, "selected").length > 0)
      .filter(group => !this.selectedSearch || this.matchesGroup(group, "selected"));
    const businesses = this.getRootBusinesses()
      .filter(business => this.getBusinessLeaves(business, "selected").length > 0)
      .filter(business => !this.selectedSearch || this.matchesBusiness(business, "selected"));
    this.setSelectedPage(this.sort([...groups, ...businesses, ...this.getRootLeaves("selected")]));
  }

  private setAvailablePage(items: ShareItem[]): void {
    const page = this.page(items, this.availablePageIndex);
    this.availablePageIndex = page.index;
    this.availableTotalPages = page.total;
    this.availablePagedGroups = page.items.filter(item => item.type === "saved-group");
    this.availablePagedBusinesses = page.items.filter(item => item.type === "business");
    this.availablePagedRootItems = page.items.filter(item => this.isLeaf(item));
  }

  private setSelectedPage(items: ShareItem[]): void {
    const page = this.page(items, this.selectedPageIndex);
    this.selectedPageIndex = page.index;
    this.selectedTotalPages = page.total;
    this.selectedPagedGroups = page.items.filter(item => item.type === "saved-group");
    this.selectedPagedBusinesses = page.items.filter(item => item.type === "business");
    this.selectedPagedRootItems = page.items.filter(item => this.isLeaf(item));
  }

  private page(items: ShareItem[], requestedIndex: number): { items: ShareItem[]; index: number; total: number } {
    const total = Math.max(1, Math.ceil(items.length / this.pageSize));
    const index = Math.min(requestedIndex, total - 1);
    const start = index * this.pageSize;
    return { items: items.slice(start, start + this.pageSize), index, total };
  }

  private matchesGroup(group: ShareItem, context: Context): boolean {
    const search = context === "available" ? this.availableSearch : this.selectedSearch;
    return this.matches(group, search) || this.getChildBusinesses(group, context).some(business => this.matchesBusiness(business, context));
  }

  private matchesBusiness(business: ShareItem, context: Context): boolean {
    const search = context === "available" ? this.availableSearch : this.selectedSearch;
    return this.matches(business, search) || this.getBusinessLeaves(business, context).some(item => this.matches(item, search));
  }

  private matches(item: ShareItem, search: string): boolean {
    const text = this.normalizeText(search);
    return !text || this.normalizeText(item.name).includes(text) || this.normalizeText(String(item.id)).includes(text);
  }

  private getDisplayedAvailableItems(): ShareItem[] {
    const result = [
      ...this.availablePagedGroups,
      ...this.availablePagedBusinesses,
      ...this.availablePagedRootItems
    ];
    this.availablePagedGroups.forEach(group => {
      if (!this.isGroupExpanded(group)) return;
      const businesses = this.getBusinesses(group);
      result.push(...businesses);
      businesses.forEach(business => {
        if (this.isBusinessExpanded(business)) result.push(...this.getBusinessItems(business));
      });
    });
    this.availablePagedBusinesses.forEach(business => {
      if (this.isBusinessExpanded(business)) result.push(...this.getBusinessItems(business));
    });
    return this.unique(result);
  }

  // -------------------------------------------------------------------------
  // Save / small helpers
  // -------------------------------------------------------------------------
  private buildRequest(): UpdateSharedAudienceRequest | null {
    const platformId = this.getPlatformId(this.sourceData);
    const transferId = (this.transfer as any)?.id ?? (this.transfer as any)?.transferId ?? this.sourceData.transferId;
    const audienceId = (this.audience as any)?.id ?? (this.transfer as any)?.audienceId;
    if (platformId == null || transferId == null || audienceId == null) {
      console.error("platformId, transferId and audienceId are required");
      return null;
    }
    return {
      platformId,
      transferId,
      audienceId,
      items: this.uniqueLeaves(this.selectedItems).map(item => ({
        id: item.id,
        name: item.name ?? "",
        businessId: item.businessId,
        businessName: item.businessName,
        type: this.leafType(item.type),
        parentId: item.parentId,
        info: item.info
      }))
    } as UpdateSharedAudienceRequest;
  }

  private getPlatformId(data: TransferShareAudience): string | number | null {
    const transfer = this.transfer as any;
    return transfer?.targetPlatform ?? transfer?.targetPlatformId ?? data?.platformId ?? data?.platforms?.[0]?.id ?? null;
  }

  private selectedLeafKeys(): Set<string> {
    return new Set(this.selectedItems.filter(item => this.isLeaf(item)).map(item => this.key(item)));
  }

  private hasSelectionChanges(): boolean {
    const current = this.selectedLeafKeys();
    return current.size !== this.initialSelectedKeys.size || [...current].some(key => !this.initialSelectedKeys.has(key));
  }

  private hasRetryableErrors(): boolean {
    return this.selectedItems.some(item => this.isLeaf(item) && item.isError === true);
  }

  private errorMessages(items: ShareItem[]): string {
    return [...new Set(items.filter(item => item.isError).map(item => item.errorMessage?.trim()).filter(Boolean))].join(" | ");
  }

  private key(item: ShareItem): string {
    return this.isLeaf(item) ? this.leafKey(item.type, item.id) : `${item.type}:${String(item.id)}`;
  }

  private leafKey(type: unknown, id: unknown): string {
    return `${this.leafType(type)}:${String(id)}`;
  }

  private leafType(type: unknown): LeafType {
    return type === "account" ? "account" : "advertiser";
  }

  private isLeaf(item?: ShareItem | null): item is LeafItem {
    return item?.type === "advertiser" || item?.type === "account";
  }

  private unique<T extends ShareItem>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter(item => {
      const key = this.key(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private uniqueLeaves(items: ShareItem[]): LeafItem[] {
    return this.unique(items.filter((item): item is LeafItem => this.isLeaf(item)));
  }

  private sort<T extends ShareItem>(items: T[]): T[] {
    const rank: Partial<Record<ShareAudienceType, number>> = {
      "saved-group": 0,
      business: 1,
      account: 2,
      advertiser: 2
    };
    return [...items].sort((a, b) =>
      ((rank[a.type] ?? 9) - (rank[b.type] ?? 9)) ||
      (a.name ?? "").localeCompare(b.name ?? "")
    );
  }

  private setChecked(store: Set<string>, key: string, checked: boolean): void {
    checked ? store.add(key) : store.delete(key);
  }

  private toggleExpanded(store: Set<string>, id: unknown): void {
    const key = String(id);
    store.has(key) ? store.delete(key) : store.add(key);
  }

  private normalizeText(value: unknown): string {
    return String(value ?? "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  private isInteractive(event: MouseEvent): boolean {
    return !!(event.target as HTMLElement | null)?.closest("input, button, a, .share-tree-toggle, .share-remove-btn");
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private resetUi(): void {
    this.availableFilter = "all";
    this.availableSearch = "";
    this.availableSearchRaw = "";
    this.selectedSearch = "";
    this.availablePageIndex = 0;
    this.selectedPageIndex = 0;

    this.availableChecked.clear();
    this.selectedChecked.clear();
    this.expandedAvailableGroups.clear();
    this.expandedAvailableBusinesses.clear();
    this.expandedSelectedGroups.clear();
    this.expandedSelectedBusinesses.clear();
  }
}